import type { OpenerGroupPodState } from '../../api/useOpenerGroupPods';

/**
 * Pure status/derivation helpers for the per-group opener pod controls
 * (GroupPodControls on the Entry page + the BucketPage rail dots). Kept
 * out of the components so the derivation matrix is unit-testable —
 * bulkScope.ts / groupFilter.ts pattern.
 */

export type GroupPodStatus = 'off' | 'provisioning' | 'running' | 'panicked';

/**
 * Derive the operator-facing status from the pod-state entry. The state
 * endpoint is fail-soft: `engineReady` may be omitted when the cluster is
 * unreachable — missing is treated as NOT ready (provisioning), never as
 * running. A group absent from the response renders as Off.
 */
export function deriveGroupPodStatus(
  state: Pick<OpenerGroupPodState, 'podsDesired' | 'engineReady'> | undefined
): GroupPodStatus {
  if (!state) return 'off';
  if (state.podsDesired === 'panicked') return 'panicked';
  if (state.podsDesired === 'off') return 'off';
  return state.engineReady === true ? 'running' : 'provisioning';
}

/**
 * Start is allowed from Off and from Panicked (hard panic has no lockout —
 * `start` flips 'panicked' → 'on'), but NEVER while the GLOBAL opener
 * panic lockout is armed: the control plane refuses group starts under
 * lockout, so the button must not offer them.
 */
export function canStartGroup(
  status: GroupPodStatus,
  globalLockoutArmed: boolean
): boolean {
  if (globalLockoutArmed) return false;
  return status === 'off' || status === 'panicked';
}

/** Stop (pause-and-drain) is always allowed while anything is desired on. */
export function canStopGroup(status: GroupPodStatus): boolean {
  return status !== 'off';
}

/** Panics (soft or hard) only make sense while the group is not Off. */
export function canPanicGroup(status: GroupPodStatus): boolean {
  return status !== 'off';
}

export const GROUP_POD_STATUS_LABELS: Record<GroupPodStatus, string> = {
  off: 'Off',
  provisioning: 'Provisioning',
  running: 'Running',
  panicked: 'Panicked'
};

export const GROUP_POD_STATUS_CHIP_COLORS: Record<
  GroupPodStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  off: 'default',
  provisioning: 'info',
  running: 'success',
  panicked: 'error'
};

/** Theme palette key for the BucketPage rail status dot. */
export const GROUP_POD_STATUS_DOT_COLORS: Record<GroupPodStatus, string> = {
  off: 'grey.700',
  provisioning: 'info.main',
  running: 'success.main',
  panicked: 'error.main'
};

export interface CapacityIndicator {
  /** e.g. "units 210 / 200 (105%)". */
  label: string;
  /** MUI Chip color: green in-range, amber near the ceiling, red over it. */
  color: 'success' | 'warning' | 'error';
  /** `units / ceiling` (0 when the ceiling is unknown). */
  pct: number;
}

/** Fraction of the ceiling at/above which the indicator turns amber (near-full). */
export const CAPACITY_WARN_FRACTION = 0.8;

/**
 * Derive the per-group leg-unit capacity indicator: how the group's expected
 * engine leg-units (tokio tasks) compare to the RUNNING tier's ceiling. This is
 * the load gauge the backend under-provision alert keys on — surfaced inline so
 * the operator sees a group approaching its ceiling BEFORE the alert fires.
 *
 * success below `CAPACITY_WARN_FRACTION`, warning from there up to the ceiling,
 * error once over it. A non-positive/absent ceiling can't be evaluated → treated
 * as unknown (success, 0%).
 */
export function deriveCapacityIndicator(
  units: number,
  ceiling: number
): CapacityIndicator {
  const pct = ceiling > 0 ? units / ceiling : 0;
  const color: CapacityIndicator['color'] =
    pct > 1 ? 'error' : pct >= CAPACITY_WARN_FRACTION ? 'warning' : 'success';
  return {
    label: `units ${units} / ${ceiling} (${Math.round(pct * 100)}%)`,
    color,
    pct
  };
}
